/**
 * Applicant / resident application UI copy (English).
 * Spanish is handled at runtime by Google Translate — not stored here.
 */
export const APPLICANT_CONTENT = {
  "common": {
    "copyright": "© 2020-2026 Liberty Rent. All Rights Reserved.",
    "selectLanguage": "Select Language",
    "accessibility": "Accessibility",
    "logo": {
      "liberty": "Liberty",
      "rent": "Rent"
    }
  },
  "login": {
    "pageTitle": "Login - Liberty Rent",
    "sandboxWarning": {
      "message": "This is a Sandbox platform. If you received an email redirecting to this URL Please visit",
      "cornerstoneLink": "https://cornerstone.libertyrent.com",
      "cornerstoneUrl": "https://cornerstone.libertyrent.com",
      "helpEmail": "help@libertyrent.com",
      "helpEmailAddress": "help@libertyrent.com"
    },
    "logo": {
      "title": "Liberty Rent",
      "subtitle": "SECOND CHANCE LEASE APPROVAL"
    },
    "form": {
      "emailLabel": "Email address",
      "emailPlaceholder": "Enter your email",
      "passwordLabel": "Password",
      "passwordPlaceholder": "Enter your password",
      "signInButton": "SIGN IN",
      "signingInButton": "SIGNING IN...",
      "forgotPassword": "Forgot Password?",
      "applyNow": "APPLY NOW"
    },
    "validation": {
      "emailRequired": "Email is required",
      "emailInvalid": "Please enter a valid email address",
      "passwordRequired": "Password is required",
      "passwordMinLength": "Password must be at least 6 characters"
    },
    "footer": {
      "chromeRecommendation": "Our system works best on Google Chrome"
    }
  },
  "forgotPassword": {
    "title": "Forgot Password",
    "instructions": "Please Allow **5 minutes** to receive your new password. Check your **Spam Folder** for the email.",
    "emailLabel": "Email",
    "emailPlaceholder": "Enter your email",
    "sendButton": "Send",
    "sendingButton": "Sending...",
    "backToLogin": "← Back to Login"
  },
  "applyNow": {
    "step1": {
      "title": "Have you applied at the property?",
      "noOption": "No",
      "noDescription": "I haven't yet",
      "yesOption": "Yes",
      "yesDescription": "And I was referred to Liberty Rent"
    },
    "step2": {
      "title": "Do you have an invite code?",
      "description": "Some communities require an invite code to use our program. If you did not receive one they probably don't require it. Later in the registration process if our system recognizes that your property requires it you will need to request one from your property.",
      "noOption": "No",
      "noDescription": "I didn't get one",
      "yesOption": "Yes",
      "yesDescription": "I have an invite code",
      "yesUsedOption": "Yes",
      "yesUsedDescription": "I have an invite code and have already used it. Please Sign In with your Email and Password.",
      "yesUsedDescriptionLine1": "I have an invite code and have already used it.",
      "yesUsedDescriptionLine2": "Please Sign In with your Email and Password"
    },
    "step3": {
      "title": "Enter your invite code below",
      "description": "We will use your invite code to lookup all of your information making the registration process quicker and easier for you.",
      "placeholder": "Enter invite code here",
      "nextButton": "Next",
      "verifying": "Verifying...",
      "validation": {
        "codeRequired": "Please enter an invite code",
        "codeInvalid": "Invalid invite code. Please try again.",
        "verifyFailed": "Failed to verify code. Please try again."
      }
    },
    "step4": {
      "title": "You need to apply with one of our partner properties first.",
      "description": "Start by applying with an apartment community. They will verify if they have units available and will determine if you need our help. If you would like to search properties we've partnered with, contact customer service at {phone} or {email}",
      "phone": "(877) 581-7368",
      "email": "help@libertyrent.com"
    },
    "goBack": "Go Back",
    "signInLink": "Already have a User Account? Sign In",
    "copyright": "© 2020 Liberty Rent. All Right Reserved."
  },
  "errors": {
    "generic": "An error occurred. Please try again.",
    "networkError": "Network error occurred",
    "applicantIdNotFound": "Applicant ID not found. Please refresh the page.",
    "saveProgressFailed": "Failed to save. Please try again.",
    "saveAddressFailed": "Failed to save address. Please try again.",
    "buildAddressDataFailed": "Failed to build address data.",
    "buildPreviousAddressDataFailed": "Failed to build previous address data.",
    "validationErrors": "Please fix the validation errors before saving.",
    "validationErrorsContinue": "Please fix the validation errors before continuing.",
    "residenceTypeRequired": "Please select a residence type.",
    "invalidDateOfBirth": "Invalid date of birth",
    "invalidInputData": "Invalid Input Data",
    "missingFields": "Missing Fields",
    "validationMessage": "Validation Message",
    "fieldRequired": {
      "streetNumber": "'Street No.' is required.",
      "zipCode": "'Zipcode' is required.",
      "zipCodeLength": "Zipcode must be 5 or 6 digits.",
      "landlordName": "'Landlord Name' is required.",
      "landlordEmail": "'Landlord Email' is required.",
      "landlordMobile": "'Landlord Mobile' is required.",
      "moveInDate": "'Move In Date' is required.",
      "plannedMoveOutDate": "'Planned Move Out Date' is required.",
      "leaseEndDate": "'Lease End Date' is required.",
      "rentAmount": "'Rent Amount' is required.",
      "monthsPaidRent": "'How many months did you pay rent?' is required.",
      "monthlyMortgagePayment": "'Monthly Mortgage Payment Amount' is required.",
      "monthsResided": "'Number of months you resided' is required.",
      "contactName": "'Contact Name' is required.",
      "contactEmail": "'Contact Email' is required.",
      "contactMobile": "'Contact Mobile' is required.",
      "contactRelationship": "'Contact Relationship' is required.",
      "city": "'City' is required.",
      "state": "'State' is required.",
      "streetName": "'Street Name' is required.",
      "residenceType": "'Type of residence' is required.",
      "dateOfBirth": "Date of Birth is required",
      "ssnOrItin": "SSN or ITIN is required",
      "ssnOrItinDigits": "SSN or ITIN must be 9 digits",
      "firstName": "First name is required",
      "lastName": "Last name is required",
      "month": "Month is required",
      "year": "Year is required",
      "cvv": "CVV is required",
      "cardNumber": "Invalid card number",
      "houseNo": "Street number is required",
      "street": "Street name is required",
      "zipCodeValid": "Valid zip code is required"
    },
    "employment": {
      "employmentType": "'Type of Employment' is required.",
      "companyName": "'Company Name' is required.",
      "jobStartDate": "'Job Start Date' is required.",
      "jobEndDate": "'Job End Date' is required.",
      "monthlyIncome": "'Monthly Income' is required.",
      "monthsAtJob": "'Number of months at this job' is required.",
      "streetName": "'Employer Street' is required.",
      "city": "'Employer City' is required.",
      "state": "'Employer State' is required.",
      "zipCode": "'Employer Zipcode' is required.",
      "country": "'Country' is required.",
      "distanceExplanation": "'Distance Explanation' is required for distances over 50 miles.",
      "distanceWarning": "Warning: The distance between your current and employer address, your new and employer address, or your current and new address exceeds 50 miles.",
      "incomeFrequency": "'Income Frequency' is required.",
      "sourceName": "'Source Name' is required.",
      "amount": "'Amount' is required.",
      "income": "'Income' is required.",
      "transactionDate": "'Transaction Date' is required.",
      "startDate": "'Start Date' is required.",
      "endDate": "'End Date or Current Active' is required.",
      "monthsOfIncome": "'Months of this Income' is required.",
      "explanationRequired": "Explanation is required."
    },
    "history": {
      "hasBeenEvicted": "'Have you been evicted?' selection is required.",
      "isBankrupt": "'Are you currently involved in a bankruptcy?' selection is required.",
      "bankruptcyType": "'What type?' is required.",
      "isBankruptcySettled": "'Is it Settled?' selection is required.",
      "bankruptcyExplanation": "'Please explain' is required.",
      "numberOfDependents": "'How many children (or other dependents) will reside with you?' is required."
    },
    "signature": {
      "governmentIdType": "'Identity Verification' type selection is required.",
      "governmentIdFiles": "'Identity Verification' file attachment is required. Please upload both front and back images for Driver's License.",
      "governmentIdFilesPassport": "'Identity Verification' file attachment is required. Please upload passport image.",
      "driversLicenseFrontSize": "Driver's License front image must be 5 MB or less.",
      "driversLicenseBackSize": "Driver's License back image must be 5 MB or less.",
      "passportImageSize": "Passport image must be 5 MB or less.",
      "fileSize": "File size must be 5 MB or less per file.",
      "fileSizeSingle": "File size must be 5 MB or less.",
      "signature": "'Signature' is required.",
      "signatureInvalid": "Enter valid name.",
      "allStatementsAgreed": "'Electronic Consent' agreement is required.",
      "allDocumentsChecked": "'Acknowledgements and Signature' must be checked.",
      "ssnDigits": "'SSN or ITIN' must be 9 digits.",
      "signatureMismatch": "Signature name must match Applicant name and Government ID. Either correct the names so they match, or contact customer service for extenuating circumstances (e.g. Power of Attorney, etc.) so we can update the records on this application. contact customer service at <a href=\"mailto:help@libertyrent.com\" target=\"_top\" class=\"text-blue-600 hover:text-blue-800 underline\">help@libertyrent.com</a> or (877) 581-7368."
    },
    "payment": {
      "firstName": "First name is required",
      "lastName": "Last name is required",
      "cardNumber": "Invalid card number",
      "expiryMonth": "Month is required",
      "expiryYear": "Year is required",
      "cvv": "CVV is required",
      "houseNo": "Street number is required",
      "street": "Street name is required",
      "city": "City is required",
      "zipCode": "Valid zip code is required"
    },
    "navigation": {
      "completePreviousSteps": "Please complete previous steps before proceeding.",
      "completeTab": "Please complete \"{tabName}\" before proceeding."
    }
  },
  "verifyInfo": {
    "title": "VERIFY YOUR INFO AND SET YOUR PASSWORD",
    "propertyInfo": "We found you! Our system shows that you applied at",
    "contactNote": "Please call us toll-free at (877) 581-7368 if any of this information (received from the property) needs to be updated.",
    "confirmNote": "Please confirm your name and date of birth, and create a unique password.",
    "legalNameNote": "Note: Please specify your Legal Name, as this will be compared to other sources later in the process.",
    "privacyNote": "Data Privacy Note: Liberty Rent will contact the applicants by Text Msg to the Phone # or Email address provided, only so that we can communicate the status of your application with our program. We do not share this information with any outside parties. Please add Liberty Rent to your contacts, so we are not blocked.",
    "emailNote": "If you do not see our email shortly, please check your Spam or Junk email folder. We recommend adding our new email address",
    "passwordStrength": "Password strength",
    "submit": "Submit",
    "submitting": "Submitting...",
    "signInLink": "Already have a User Account? Sign In",
    "banner": {
      "title": "How does the Liberty Rent® program work?",
      "description": "Fill out the application and pay a $35 application fee. The application fee is refundable if you are not approved by Liberty Rent®. If you are approved:",
      "step1": "You will be required to pay an Access Fee to Liberty Rent. This fee is non-refundable and is in addition to any other move-in fees required by the property (first month rent, deposits, etc.).",
      "step2": "The property will issue your lease ONLY after the Access Fee is paid in full.",
      "step3": "You are still responsible for paying monthly rent to the property and in accordance with the lease agreement.",
      "eligibleTitle": "To be eligible to participate in the Liberty Rent® program,",
      "eligibleDescription": "you must:",
      "eligible1": "Have a valid social security number or ITIN.",
      "eligible2": "Not be currently involved in an open bankruptcy or an open eviction."
    },
    "sections": {
      "personalInfo": "Personal Information",
      "contactInfo": "Contact Information",
      "setPassword": "Set Password"
    },
    "fields": {
      "firstName": "FIRST NAME",
      "middleName": "MIDDLE NAME",
      "lastName": "LAST NAME",
      "dob": "DOB",
      "email": "EMAIL",
      "confirmEmail": "CONFIRM EMAIL",
      "mobilePhone": "MOBILE PHONE",
      "sharedPhone": "Click here if this is a shared phone number.",
      "password": "PASSWORD",
      "confirmPassword": "CONFIRM PASSWORD"
    },
    "validation": {
      "firstNameRequired": "First name is required",
      "lastNameRequired": "Last name is required",
      "dobRequired": "Date of birth is required",
      "emailRequired": "Email is required",
      "emailInvalid": "Please enter a valid email address",
      "confirmEmailRequired": "Please confirm your email",
      "emailsMustMatch": "Emails must match",
      "mobilePhoneRequired": "Mobile phone is required",
      "passwordMinLength": "Password must be at least 8 characters",
      "passwordUppercase": "Password must contain at least one uppercase letter",
      "passwordLowercase": "Password must contain at least one lowercase letter",
      "passwordNumber": "Password must contain at least one number",
      "confirmPasswordRequired": "Please confirm your password",
      "passwordsMustMatch": "Passwords must match"
    }
  },
  "application": {
    "title": "Resident Application",
    "subtitle": "Complete your application step by step",
    "steps": {
      "about": {
        "name": "About Yourself",
        "shortName": "About Yourself",
        "title": "Your Personal Profile Info *"
      },
      "address": {
        "name": "Current Living Address",
        "shortName": "Current Living Address",
        "title": "Current Living Address *"
      },
      "property": {
        "name": "Property *",
        "shortName": "Property",
        "title": "Where Are You Moving To?",
        "subtitle": "Tell us about the property you're applying for"
      },
      "income": {
        "name": "Income and Employment",
        "shortName": "Income and Employment",
        "title": "Income and Employment"
      },
      "history": {
        "name": "Personal History",
        "shortName": "Personal History/Situation",
        "title": "Personal History / Situation"
      },
      "signature": {
        "name": "Acknowledgements and Signature",
        "shortName": "Acknowledgements and Signature",
        "title": "Acknowledgements and Signature",
        "subtitle": "Review and sign your application"
      },
      "payment": {
        "name": "Payment",
        "shortName": "Payment",
        "title": "Payment"
      }
    },
    "sections": {
      "personalProfile": {
        "title": "Your Personal Profile Info *",
        "fields": {
          "firstName": "First Name",
          "middleName": "Middle Name",
          "lastName": "Last Name",
          "suffix": "Suffix",
          "mobilePhone": "Mobile Phone",
          "sharedPhone": "This is a shared phone#",
          "email": "Email",
          "communicationPreference": "Communication Preference",
          "textMsg": "Text Msg",
          "emailOption": "Email",
          "placeholder": {
            "firstName": "First Name",
            "middleName": "Middle Name",
            "lastName": "Last Name",
            "suffix": "Suffix",
            "mobilePhone": "(000) 000-0000",
            "email": "email@example.com",
            "dateOfBirth": "MM/DD/YYYY",
            "ssnOrItin": "SSN or ITIN"
          },
          "dateOfBirth": "Date of Birth",
          "ssnOrItin": "SSN or ITIN* (Do not include dashes)"
        },
        "save": "Save"
      },
      "idVerification": {
        "title": "ID Verification",
        "subtitle": "Verify your identity with a government-issued ID",
        "startButton": "Start Identity Verification",
        "continueButton": "Continue Identity Verification",
        "preparing": "Preparing identity verification...",
        "confirmTitle": "Continue to Identity Verification?",
        "confirmMessage": "You'll be redirected to our trusted third-party verification provider to complete the identity check. Once verification is complete, you'll return here automatically.",
        "confirmOk": "OK",
        "confirmCancel": "Cancel",
        "useDocumentUploadsLink": "Use document & photo uploads instead",
        "documentUploadsBlockedWithSession":
          "Your identity verification is in review. Please wait for the review to complete, or contact customer service if you need help.",
        "useVerifastVerificationLink": "Use identity verification instead",
        "documentUploadSubtitle": "Upload your driver's license or passport",
        "successTitle": "ID Verification Successful",
        "successBody": "Your identity has been verified. You may save and continue.",
        "reviewTitle": "ID Verification In Review",
        "reviewBody": "Your identity is being reviewed. You can continue once verification is complete.",
        "unsuccessfulTitle": "ID Verification Unsuccessful",
        "unsuccessfulBody": "Unfortunately ID verification was unable to verify your identity. Please contact customer service at",
        "failedUploadHint": "You can continue by uploading your driver's license or passport below.",
        "supportPhone": "(877) 581-7368",
        "supportPhoneTel": "8775817368",
        "supportEmail": "help@libertyrent.com",
        "required": "ID Verification is required. Please complete identity verification.",
        "loading": "Identity verification is loading. Please wait a moment before continuing.",
        "failed": "ID Verification was unsuccessful. Please contact customer service."
      },
      "currentAddress": {
        "title": "Current Living Address *",
        "subtitle": "We require two years of address history",
        "address": "Address",
        "otherOutsideUS": "Other / Outside U.S.",
        "streetNo": "Street No.",
        "streetName": "Street Name* (ex. Main)",
        "aptUnit": "Apt/Unit",
        "city": "City",
        "state": "State",
        "zipcode": "Zipcode",
        "country": "Country",
        "residenceType": "Type of residence",
        "rent": "Rent",
        "own": "Own",
        "familyOrOther": "Family Or Other",
        "duration": "Duration",
        "months": "Months",
        "otherAddress": {
          "title": "Other Address for Credit Search (Optional)",
          "description": "If for some reason, we are unable to match your address(es) above to your credit file, you may enter another address here.",
          "noAlternateAddress": "I don't have an alternate address in the US",
          "addOtherAddress": "Add Other Address"
        },
        "rentFields": {
          "landlordName": "Landlord Name",
          "landlordEmail": "Landlord Email",
          "landlordMobile": "Landlord Mobile",
          "moveInDate": "Move In Date",
          "stillHere": "Still here",
          "leaseEndDate": "Lease End Date",
          "rentAmount": "Rent Amount",
          "monthsPaidRent": "How many months did you pay rent?"
        },
        "ownFields": {
          "moveInDate": "Move In Date",
          "stillHere": "Still here",
          "plannedMoveOutDate": "Planned Move Out Date",
          "monthlyMortgagePayment": "Monthly Mortgage Payment Amount",
          "monthsResided": "Number of months you resided at this address"
        },
        "familyFields": {
          "contactName": "Contact Name",
          "contactRelationship": "Contact Relationship",
          "contactEmail": "Contact Email",
          "contactMobile": "Contact Mobile",
          "moveInDate": "Move In Date",
          "stillHere": "Still here",
          "plannedMoveOutDate": "Planned Move Out Date",
          "rentAmount": "Rent Amount",
          "monthsPaidRent": "How many months did you pay rent?"
        }
      },
      "previousAddress": {
        "title": "Previous Address",
        "subtitle": "You have not provided 24 months of address history. Please add another.",
        "addressLabel": "Previous Address",
        "addressPlaceholder": "Enter your previous address"
      },
      "property": {
        "title": "Where Are You Moving To?",
        "hasApplied": "Have you applied to a property?",
        "yes": "Yes",
        "no": "No",
        "note": "Note: If you are unsure or if you have been told an invitation has been generated for you, simply click 'Look Up'. If you've typed in a code then click 'Validate' to populate your information.",
        "inviteCode": "Have an Invite",
        "warning": "Please stop your application and apply at your property's rental office before you continue your application.",
        "propertyName": "Property Name",
        "street": "Street",
        "city": "City",
        "state": "State",
        "zipcode": "Zipcode",
        "monthlyRentAmount": "Monthly Rent Amount",
        "leaseStartDate": "Lease Start Date",
        "leaseDuration": "Lease Duration",
        "leasingConsultant": "Leasing Consultant",
        "leasingConsultantEmail": "Leasing Consultant/Property Manager Email",
        "leasingConsultantPhone": "Leasing Consultant/Property Manager Phone",
        "consultantNote": "If the Leasing Consultant is unknown, select \"Other\", and in the name field type \"unknown\"."
      },
      "coResidents": {
        "title": "Co-Residents",
        "subtitle": "Additional residents on the lease",
        "question": "Will other individuals be on this lease? (Do not include children under the age of 18)",
        "yes": "Yes",
        "no": "No"
      }
    },
    "buttons": {
      "back": "Back",
      "next": "Next",
      "nextStep": "Next",
      "save": "Save",
      "saveContinue": "Save & Continue",
      "saveExit": "Save & Exit",
      "exit": "Exit",
      "submit": "Submit Application",
      "viewDetail": "View Detail",
      "closeDetail": "Close Detail"
    },
    "messages": {
      "saveProgressSuccess": "Progress saved successfully!",
      "saveAddressSuccess": "Address saved successfully!",
      "saveAddressesSuccess": "Current and previous addresses saved successfully!",
      "saving": "Saving...",
      "loading": "Loading application data..."
    },
    "status": {
      "creditReportPending": "Credit Report Pending",
      "incomplete": "Incomplete",
      "complete": "Complete",
      "rejected": "Rejected"
    },
    "applicantStatus": "Applicant Status",
    "applicantId": "Applicant ID",
    "applicationId": "Application ID",
    "contractPayment": "Contract Payment",
    "confirmed": "Confirmed",
    "paymentDueDate": "Payment Due Date",
    "coApplicants": "Co-Applicant(s)"
  }
} as const
